import { createStyles, makeStyles } from "@material-ui/styles";
import classnames from "classnames";
import * as React from "react";

import { mergeClasses, RelodeTheme, Text } from "../";
import { LogoOnly } from "../../assets/logos";
import { BlurLoader, FadeLoader } from "./";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                display: "flex",
                flexDirection: "column",
                position: "relative",
                alignContent: "center",
                justifyContent: "center",

                "& > svg": {
                    animation: "spin 0.8s linear infinite",
                },
            },
            loader: {},
            loading: {
                pointerEvents: "none",
            },
            loaderLogoAndMessage: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                position: "absolute",
            },
            loaderLogo: {
                animation: "spin 0.8s linear infinite",
                marginBottom: theme.spacing(2),
                width: 64,
                height: 64,
            },
            loaderMessage: {
                color: theme.relode.colors.backgroundText.main,
            },
            blurLoaderLogoAndMessage: {
                backgroundColor: "rgba(255,255,255,0.7)",
            },
            fillSpace: {
                width: "100%",
                height: "100%",
                flexGrow: 1,
            },
            mobileLoadingFillSpace: {
                [theme.breakpoints.down("sm")]: {
                    position: "fixed",
                    top: 0,
                    left: 0,
                    height: "100vh",
                    width: "100vw",
                },
            },
            loadingJobGPT: {
                "&:after": {
                    content: "''",
                    animation: "updateJobContent 45s linear",
                    color: theme.relode.colors.backgroundText.main,
                },
            },
            loadingCandidateGPT: {
                "&:after": {
                    content: "''",
                    animation: "updateCandidateContent 45s linear",
                    color: theme.relode.colors.backgroundText.main,
                },
            },
        }),
    { name: "Loading" }
);

interface LoadingProps {
    isLoading?: boolean;
    children?: React.ReactNode;
    variant?: "text" | "tags" | "main" | "blur" | "fade";
    showIndicator?: boolean;
    fillSpace?: boolean;
    delay?: number | "none";
    displayText?: string;
    classes?: Partial<ReturnType<typeof useStyles>>;
    messageType?: LoadingMessageType;
}

export type LoadingMessageType = "default" | "jobGPT" | "candidateGPT";

export const Loading = (props: LoadingProps) => {
    const {
        isLoading,
        children,
        variant,
        showIndicator,
        fillSpace = true,
        delay = 200,
        displayText = "please wait",
        classes: classesProp,
        messageType = "default",
    } = props;

    const classes = mergeClasses(useStyles({}), classesProp);
    const [showLoader, setShowLoader] = React.useState(false);
    const timeout = React.useRef({} as any);

    const setWithDelay = React.useMemo(() => {
        if (delay !== "none" && delay > 0) {
            return () =>
                (timeout.current = setTimeout(
                    () => setShowLoader(true),
                    delay
                ));
        } else {
            return () => setShowLoader(true);
        }
    }, [delay]);

    React.useEffect(() => {
        if (isLoading) {
            setWithDelay();
        } else {
            setShowLoader(false);
            clearTimeout(timeout.current);
        }
        return () => {
            clearTimeout(timeout.current);
        };
    }, [isLoading, setWithDelay]);

    let Loader: React.ComponentType<any> = BlurLoader;
    switch (variant) {
        case "blur":
            Loader = BlurLoader;
            break;

        case "fade":
            Loader = FadeLoader;
            break;
    }

    const LoaderMessage = () => {
        switch (messageType) {
            case "jobGPT":
                return (
                    <Text variant="h4" className={classes.loadingJobGPT}></Text>
                );
            case "candidateGPT":
                return (
                    <Text variant="h4" className={classes.loadingCandidateGPT}></Text>
                );
        }
        return (
            <Text variant="h4" className={classes.loaderMessage}>
                {displayText}
            </Text>
        );
    };
 

    const rootClasses = classnames({
        [classes.root]: true,
        [classes.fillSpace]: fillSpace,
        [classes.loading]: isLoading,
        [classes.mobileLoadingFillSpace]: isLoading && fillSpace,
    });

    const loaderLogoAndMessageClasses = classnames({
        [classes.loaderLogoAndMessage]: true,
        [classes.blurLoaderLogoAndMessage]: variant === "blur",
    });

    return (
        <div className={rootClasses}>
            <Loader classes={{ root: classes.loader }} isLoading={showLoader}>
                {children}
            </Loader>
            {showLoader && showIndicator && (
                <div className={loaderLogoAndMessageClasses}>
                    <LogoOnly className={classes.loaderLogo} />
                    <LoaderMessage />
                </div>
            )}
        </div>
    );
};
